import { AbstractControl, FormArray, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from "@angular/forms";

import type {
  CustomerMetafieldDefinitionDto,
  ProfileFormDto,
  ProfileFormFieldDto,
  ProfileFormFieldType,
  ProfileFormMetafieldType,
  ProfileFormOptionDto,
  ProfileFormSectionDto,
  ProfileFormTargetDto,
  ProfileFormTargetKind,
  ProfileFormUnlinkableReason,
  ProfileFormUserDataKey,
} from "../../services/api";
import type { TranslationStatus } from "../../store/program/selectors";

// Translations API entity types of a form: entityId is the section, field or option uuid
export const PROFILE_FORM_TRANSLATION_ENTITY_TYPES = ["profile_form_section", "profile_form_field", "profile_form_option"] as const;
export type ProfileFormTranslationEntityType = (typeof PROFILE_FORM_TRANSLATION_ENTITY_TYPES)[number];

// Why the API rejects saving a form (the `message` of its error body; `fieldId` names the question when it can). The
// generated client does not list them: the structural ones come from the flow's validator, the rest from the save.
export const PROFILE_FORM_SAVE_ERROR_CODES = [
  "PROFILE_FORM_VERSION_CONFLICT",
  "PROFILE_FORM_FIELD_IMMUTABLE",
  "PROFILE_FORM_METAFIELD_NOT_FOUND",
  "PROFILE_FORM_METAFIELD_UNLINKABLE",
  "PROFILE_FORM_METAFIELD_TYPE_MISMATCH",
  "PROFILE_FORM_OPTION_NOT_IN_CHOICES",
  "PROFILE_FORM_TOO_MANY_FIELDS",
  "PROFILE_FORM_DUPLICATE_ID",
  "PROFILE_FORM_USER_DATA_TYPE_MISMATCH",
  "PROFILE_FORM_APP_METAFIELD",
  "PROFILE_FORM_RESERVED_METAFIELD",
  "PROFILE_FORM_DUPLICATE_TARGET",
  "PROFILE_FORM_INVALID_SELECTION_BOUNDS",
] as const;
export type ProfileFormSaveErrorCode = (typeof PROFILE_FORM_SAVE_ERROR_CODES)[number];

export const PROFILE_FORM_LIMITS = {
  sections: 10,
  fieldsPerSection: 15,
  fields: 50,
  options: 20,
  title: 255,
  description: 1000,
  label: 255,
  help: 1000,
  option: 255,
} as const;

export const PROFILE_FORM_FIELD_TYPES: ProfileFormFieldType[] = ["select", "multiselect", "checkbox", "text", "number", "date"];

// Shopify metafield types each field type can be written to
export const PROFILE_FORM_METAFIELD_TYPES: Record<ProfileFormFieldType, ProfileFormMetafieldType[]> = {
  select: ["single_line_text_field"],
  multiselect: ["list.single_line_text_field"],
  checkbox: ["boolean"],
  text: ["single_line_text_field", "multi_line_text_field"],
  number: ["number_integer", "number_decimal"],
  date: ["date"],
};

export const PROFILE_FORM_USER_DATA_KEYS: ProfileFormUserDataKey[] = ["firstName", "lastName", "phone", "birthdate"];

// Field type each customer profile attribute is asked with
export const PROFILE_FORM_USER_DATA_TYPES: Record<ProfileFormUserDataKey, ProfileFormFieldType> = {
  firstName: "text",
  lastName: "text",
  phone: "text",
  birthdate: "date",
};

export const JERICOMMERCE_METAFIELD_NAMESPACE = "jericommerce";
export const METAFIELD_KEY_PATTERN = /^[A-Za-z0-9_-]{2,64}$/;

// Why a target cannot be picked for a field: the API's reasons, plus the ones the form decides
export type ProfileFormTargetBlocker = ProfileFormUnlinkableReason | "INCOMPATIBLE_TYPE" | "ALREADY_USED";

export interface ProfileFormOptionControls {
  id: FormControl<string>;
  value: FormControl<string>;
  label: FormControl<string>;
}
export type ProfileFormOptionGroup = FormGroup<ProfileFormOptionControls>;

export interface ProfileFormTargetControls {
  kind: FormControl<ProfileFormTargetKind | null>;
  key: FormControl<string>;
  namespace: FormControl<string>;
  type: FormControl<ProfileFormMetafieldType | null>;
}
export type ProfileFormTargetGroup = FormGroup<ProfileFormTargetControls>;

export interface ProfileFormFieldControls {
  id: FormControl<string>;
  label: FormControl<string>;
  help: FormControl<string>;
  type: FormControl<ProfileFormFieldType>;
  required: FormControl<boolean>;
  minSelected: FormControl<number | null>;
  maxSelected: FormControl<number | null>;
  options: FormArray<ProfileFormOptionGroup>;
  target: ProfileFormTargetGroup;
}
export type ProfileFormFieldGroup = FormGroup<ProfileFormFieldControls>;

export interface ProfileFormSectionControls {
  id: FormControl<string>;
  title: FormControl<string>;
  description: FormControl<string>;
  required: FormControl<boolean>;
  fields: FormArray<ProfileFormFieldGroup>;
}
export type ProfileFormSectionGroup = FormGroup<ProfileFormSectionControls>;
export type ProfileFormSectionsArray = FormArray<ProfileFormSectionGroup>;

type TargetValue = Pick<ProfileFormTargetDto, "key" | "namespace"> & { kind: ProfileFormTargetKind | null };

export type DefinitionsGetter = () => CustomerMetafieldDefinitionDto[] | null;

// A section or field still being built: a new field has no target yet, a new option no texts
export type ProfileFormFieldDraft = Partial<Omit<ProfileFormFieldDto, "options">> & { options?: Partial<ProfileFormOptionDto>[] };
export type ProfileFormSectionDraft = Partial<Omit<ProfileFormSectionDto, "fields">> & { fields?: ProfileFormFieldDraft[] };

// Texts of one locale, by entity (section, field or option uuid) and field (title, label…)
export type ProfileFormLocaleTexts = Record<string, Record<string, string>>;

export interface ProfileFormSourceText {
  entityType: ProfileFormTranslationEntityType;
  entityId: string;
  field: string;
  value: string;
}

// A rejected save of the form: the API's code, and the field it is about when known
export interface ProfileFormSaveError {
  code: string;
  fieldId: string | null;
}

export interface SavedProfileFormField {
  type: ProfileFormFieldType;
  target: string | null;
}

export const hasOptions = (type: ProfileFormFieldType): boolean => type === "select" || type === "multiselect";

const notBlank: ValidatorFn = (control: AbstractControl<string>): ValidationErrors | null =>
  (control.value ?? "").trim() ? null : { required: true };

export const targetValue = (target: TargetValue): string | null => {
  if (target.kind === "userData") return `userData:${target.key}`;
  if (target.kind === "metafield") return `metafield:${target.namespace}.${target.key}`;
  return null;
};

export const definitionValue = (definition: Pick<CustomerMetafieldDefinitionDto, "namespace" | "key">): string =>
  `metafield:${definition.namespace}.${definition.key}`;

export const findDefinition = (
  definitions: CustomerMetafieldDefinitionDto[] | null,
  target: TargetValue,
): CustomerMetafieldDefinitionDto | null =>
  target.kind !== "metafield" ? null : definitions?.find(({ namespace, key }) => namespace === target.namespace && key === target.key) ?? null;

export const isTargetCompatible = (type: ProfileFormFieldType, target: TargetValue & { type?: ProfileFormMetafieldType | null }): boolean => {
  if (target.kind === "userData") return PROFILE_FORM_USER_DATA_TYPES[target.key as ProfileFormUserDataKey] === type;
  if (target.kind === "metafield") return !!target.type && PROFILE_FORM_METAFIELD_TYPES[type].includes(target.type);
  return false;
};

export const optionsOutsideChoices = (options: Pick<ProfileFormOptionDto, "value">[], choices: string[] | undefined): string[] =>
  !choices?.length ? [] : options.map(({ value }) => value.trim()).filter(value => !!value && !choices.includes(value));

const invalidSelectionBounds = (field: Pick<ProfileFormFieldDto, "minSelected" | "maxSelected"> & { options?: unknown[] }): boolean => {
  const count = field.options?.length ?? 0;
  const min = field.minSelected ?? 0;
  const max = field.maxSelected ?? count;
  // Counts of options: whole numbers only (the input's step does not validate)
  return !Number.isInteger(min) || !Number.isInteger(max) || min < 0 || max < 0 || min > max || max > count;
};

// What a metafield definition lacks to be linked to this field, or null when it can be
export const metafieldBlocker = (
  definition: CustomerMetafieldDefinitionDto,
  type: ProfileFormFieldType,
  usedTargets: Set<string>,
  ownTarget: string | null,
): ProfileFormTargetBlocker | null => {
  if (!definition.linkable) return definition.unlinkableReason ?? "UNSUPPORTED_TYPE";
  if (!definition.compatibleFieldTypes.includes(type)) return "INCOMPATIBLE_TYPE";
  const value = definitionValue(definition);
  return value !== ownTarget && usedTargets.has(value) ? "ALREADY_USED" : null;
};

// Rules across the controls of a field: the options of a select, the selection bounds, a target that fits the type
export const profileFormFieldValidator = (definitions: DefinitionsGetter): ValidatorFn => (control: AbstractControl): ValidationErrors | null => {
  const field = (control as ProfileFormFieldGroup).getRawValue();
  const errors: ValidationErrors = {};

  if (hasOptions(field.type)) {
    if (field.options.length === 0) errors["optionsRequired"] = true;
    const values = field.options.map(({ value }) => value.trim()).filter(Boolean);
    if (new Set(values).size !== values.length) errors["duplicateOptionValues"] = true;
  }
  if (field.type === "multiselect" && invalidSelectionBounds(field)) errors["selectionBounds"] = true;

  if (!field.target.kind) {
    errors["targetRequired"] = true;
  } else if (!isTargetCompatible(field.type, field.target)) {
    errors["targetIncompatible"] = true;
  } else if (hasOptions(field.type) && optionsOutsideChoices(field.options, findDefinition(definitions(), field.target)?.choices).length) {
    errors["optionsNotInChoices"] = true;
  }

  return Object.keys(errors).length ? errors : null;
};

const sectionValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null =>
  (control as ProfileFormSectionGroup).controls.fields.length ? null : { fieldsRequired: true };

export const createOptionGroup = (option: Partial<ProfileFormOptionDto> = {}): ProfileFormOptionGroup =>
  new FormGroup<ProfileFormOptionControls>({
    id: new FormControl(option.id ?? crypto.randomUUID(), { nonNullable: true }),
    value: new FormControl(option.value ?? "", { nonNullable: true, validators: [notBlank, Validators.maxLength(PROFILE_FORM_LIMITS.option)] }),
    label: new FormControl(option.label ?? "", { nonNullable: true, validators: [notBlank, Validators.maxLength(PROFILE_FORM_LIMITS.option)] }),
  });

// Controls the answer type does not show (options of a non-choice question, bounds of a single choice) are disabled:
// hidden, they must not keep the form invalid, and they come back if the merchant picks that type again
export const syncFieldTypeControls = (field: ProfileFormFieldGroup): void => {
  const type = field.controls.type.value;
  const toggle = (control: AbstractControl, shown: boolean): void => {
    if (shown && control.disabled) control.enable({ emitEvent: false });
    if (!shown && control.enabled) control.disable({ emitEvent: false });
  };
  toggle(field.controls.options, hasOptions(type));
  toggle(field.controls.minSelected, type === "multiselect");
  toggle(field.controls.maxSelected, type === "multiselect");
  field.updateValueAndValidity();
};

export const createFieldGroup = (field: ProfileFormFieldDraft, definitions: DefinitionsGetter): ProfileFormFieldGroup => {
  const group = new FormGroup<ProfileFormFieldControls>(
    {
      id: new FormControl(field.id ?? crypto.randomUUID(), { nonNullable: true }),
      label: new FormControl(field.label ?? "", { nonNullable: true, validators: [notBlank, Validators.maxLength(PROFILE_FORM_LIMITS.label)] }),
      help: new FormControl(field.help ?? "", { nonNullable: true, validators: [Validators.maxLength(PROFILE_FORM_LIMITS.help)] }),
      type: new FormControl<ProfileFormFieldType>(field.type ?? "select", { nonNullable: true }),
      required: new FormControl(field.required ?? true, { nonNullable: true }),
      minSelected: new FormControl<number | null>(field.minSelected ?? null, [Validators.min(0)]),
      maxSelected: new FormControl<number | null>(field.maxSelected ?? null, [Validators.min(0)]),
      options: new FormArray((field.options ?? []).map(option => createOptionGroup(option))),
      target: new FormGroup<ProfileFormTargetControls>({
        kind: new FormControl<ProfileFormTargetKind | null>(field.target?.kind ?? null),
        key: new FormControl(field.target?.key ?? "", { nonNullable: true }),
        namespace: new FormControl(field.target?.namespace ?? "", { nonNullable: true }),
        type: new FormControl<ProfileFormMetafieldType | null>(field.target?.type ?? null),
      }),
    },
    { validators: profileFormFieldValidator(definitions) },
  );
  syncFieldTypeControls(group);
  return group;
};

export const createSectionGroup = (section: ProfileFormSectionDraft, definitions: DefinitionsGetter): ProfileFormSectionGroup =>
  new FormGroup<ProfileFormSectionControls>(
    {
      id: new FormControl(section.id ?? crypto.randomUUID(), { nonNullable: true }),
      title: new FormControl(section.title ?? "", { nonNullable: true, validators: [notBlank, Validators.maxLength(PROFILE_FORM_LIMITS.title)] }),
      description: new FormControl(section.description ?? "", {
        nonNullable: true,
        validators: [Validators.maxLength(PROFILE_FORM_LIMITS.description)],
      }),
      required: new FormControl(section.required ?? false, { nonNullable: true }),
      fields: new FormArray((section.fields ?? []).map(field => createFieldGroup(field, definitions))),
    },
    { validators: sectionValidator },
  );

// A saved field keeps its type and target (PROFILE_FORM_FIELD_IMMUTABLE): those controls are disabled
// With the stored questions, a locked one takes back the type and target the server has (an edit made while the save
// was on its way never reached it, and could not be sent again)
export const lockSavedFields = (sections: ProfileFormSectionsArray, savedIds: Set<string>, stored?: ProfileFormDto): void => {
  const storedFields = new Map((stored?.sections ?? []).flatMap(section => section.fields).map(field => [field.id, field]));
  for (const section of sections.controls) {
    for (const field of section.controls.fields.controls) {
      const locked = savedIds.has(field.controls.id.value);
      const storedField = storedFields.get(field.controls.id.value);
      if (locked && storedField) {
        field.controls.type.setValue(storedField.type, { emitEvent: false });
        field.controls.target.setValue(
          { kind: storedField.target.kind, key: storedField.target.key, namespace: storedField.target.namespace ?? "", type: storedField.target.type ?? null },
          { emitEvent: false },
        );
        syncFieldTypeControls(field);
      }
      for (const control of [field.controls.type, field.controls.target]) {
        if (locked) control.disable({ emitEvent: false });
        else control.enable({ emitEvent: false });
      }
    }
  }
};

// Rebuilds the controls from a stored form (or none) and leaves them pristine
export const setProfileFormSections = (
  sections: ProfileFormSectionsArray,
  value: ProfileFormSectionDraft[],
  definitions: DefinitionsGetter,
  savedIds: Set<string>,
): void => {
  sections.clear({ emitEvent: false });
  value.forEach(section => sections.push(createSectionGroup(section, definitions), { emitEvent: false }));
  lockSavedFields(sections, savedIds);
  sections.updateValueAndValidity();
  sections.markAsPristine();
  sections.markAsUntouched();
};

// A working copy of the form for an editor that can be cancelled: every value as it is (texts untrimmed, the options a
// question's type hides, a question without target yet), the saved questions locked, nothing pristine lost
export const copyProfileFormSections = (
  sections: ProfileFormSectionsArray,
  definitions: DefinitionsGetter,
  savedIds: Set<string>,
): ProfileFormSectionsArray => {
  const copy = new FormArray<ProfileFormSectionGroup>(
    sections.getRawValue().map(section =>
      createSectionGroup(
        {
          ...section,
          fields: section.fields.map(field => ({
            ...field,
            minSelected: field.minSelected ?? undefined,
            maxSelected: field.maxSelected ?? undefined,
          })),
        },
        definitions,
      ),
    ),
    { validators: sections.validator ?? undefined },
  );
  lockSavedFields(copy, savedIds);
  copy.updateValueAndValidity();
  return copy;
};

const toTarget = (target: TargetValue & { type: ProfileFormMetafieldType | null }): ProfileFormTargetDto =>
  target.kind === "userData"
    ? { kind: "userData", key: target.key }
    : { kind: "metafield", namespace: target.namespace, key: target.key, type: target.type ?? undefined };

const withText = <K extends string>(key: K, value: string): Partial<Record<K, string>> =>
  (value.trim() ? { [key]: value.trim() } : {}) as Partial<Record<K, string>>;

// The `form.sections` the API expects: trimmed texts, options and bounds only where the type has them
export const toProfileFormSections = (sections: ProfileFormSectionsArray): ProfileFormSectionDto[] =>
  sections.getRawValue().map(section => ({
    id: section.id,
    title: section.title.trim(),
    ...withText("description", section.description),
    required: section.required,
    fields: section.fields.map(field => ({
      id: field.id,
      label: field.label.trim(),
      ...withText("help", field.help),
      type: field.type,
      required: field.required,
      ...(field.type === "multiselect" && field.minSelected !== null ? { minSelected: field.minSelected } : {}),
      ...(field.type === "multiselect" && field.maxSelected !== null ? { maxSelected: field.maxSelected } : {}),
      ...(hasOptions(field.type)
        ? { options: field.options.map(option => ({ id: option.id, value: option.value.trim(), label: option.label.trim() })) }
        : {}),
      target: toTarget(field.target),
    })),
  }));

// First section of a form that replaces the fixed fields: the fields the flow asked for, written to the customer profile
export const seedProfileFormSection = (
  requiredFields: ProfileFormUserDataKey[],
  texts: { title: string; labels: Record<ProfileFormUserDataKey, string> },
): ProfileFormSectionDraft => ({
  id: crypto.randomUUID(),
  title: texts.title,
  required: true,
  fields: requiredFields.length
    ? requiredFields.map(key => ({
      id: crypto.randomUUID(),
      label: texts.labels[key],
      type: PROFILE_FORM_USER_DATA_TYPES[key],
      required: true,
      target: { kind: "userData", key },
    }))
    : [{ id: crypto.randomUUID(), label: "", type: "select", required: true, options: [] }],
});

// The fixed fields a form still is: one required section of required customer profile questions with their default
// labels and no description or help. Saved that way (`requiredFields`), the flow keeps working as before for the API and
// the hubs; anything more (a metafield, another section, an optional part, a text of the merchant's) needs `form`.
export const legacyRequiredFields = (
  sections: ProfileFormSectionDto[],
  defaultLabels: Record<ProfileFormUserDataKey, string>,
): ProfileFormUserDataKey[] | null => {
  if (sections.length !== 1) return null;
  const [section] = sections;
  if (!section.required || section.description || !section.fields.length) return null;
  const keys: ProfileFormUserDataKey[] = [];
  for (const field of section.fields) {
    const key = field.target.key as ProfileFormUserDataKey;
    if (field.target.kind !== "userData" || !field.required || field.help || field.label !== defaultLabels[key]) return null;
    keys.push(key);
  }
  return keys;
};

export const savedProfileFormFields = (form: ProfileFormDto | null | undefined): Record<string, SavedProfileFormField> =>
  Object.fromEntries(
    (form?.sections ?? []).flatMap(section => section.fields).map(field => [field.id, { type: field.type, target: targetValue(field.target) }]),
  );

// A metafield key from the field's label, free among the store's `jericommerce` keys
export const suggestMetafieldKey = (label: string, takenKeys: string[]): string => {
  const base = label
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 60) || "profile_field";
  const key = base.length >= 2 ? base : `${base}_field`;
  let candidate = key;
  for (let suffix = 2; takenKeys.includes(candidate); suffix++) candidate = `${key}_${suffix}`;
  return candidate;
};

// The field a rejected save points at, found from the API's code when its body names none
export const locateProfileFormError = (
  code: string,
  sections: ProfileFormSectionDto[],
  definitions: CustomerMetafieldDefinitionDto[] | null,
  saved: Record<string, SavedProfileFormField>,
): string | null => {
  const fields = sections.flatMap(section => section.fields);
  const definitionOf = (field: ProfileFormFieldDto): CustomerMetafieldDefinitionDto | null => findDefinition(definitions, field.target);
  const find = (predicate: (field: ProfileFormFieldDto) => boolean): string | null => fields.find(predicate)?.id ?? null;

  switch (code) {
    case "PROFILE_FORM_DUPLICATE_TARGET": {
      const seen = new Set<string>();
      return find(field => {
        const value = targetValue(field.target);
        if (!value) return false;
        if (seen.has(value)) return true;
        seen.add(value);
        return false;
      });
    }
    case "PROFILE_FORM_USER_DATA_TYPE_MISMATCH":
    case "PROFILE_FORM_METAFIELD_TYPE_MISMATCH":
      return find(field => !isTargetCompatible(field.type, field.target));
    case "PROFILE_FORM_APP_METAFIELD":
      return find(field => field.target.kind === "metafield" && (/^(app--|\$app)/.test(field.target.namespace ?? "") || definitionOf(field)?.unlinkableReason === "APP_OWNED"));
    case "PROFILE_FORM_RESERVED_METAFIELD":
      return find(field => definitionOf(field)?.unlinkableReason === "RESERVED_KEY");
    case "PROFILE_FORM_METAFIELD_UNLINKABLE":
      return find(field => definitionOf(field)?.linkable === false);
    case "PROFILE_FORM_METAFIELD_NOT_FOUND":
      return find(field => field.target.kind === "metafield" && !!definitions && !definitionOf(field));
    case "PROFILE_FORM_INVALID_SELECTION_BOUNDS":
      return find(field => field.type === "multiselect" && invalidSelectionBounds(field));
    case "PROFILE_FORM_OPTION_NOT_IN_CHOICES":
      return find(field => optionsOutsideChoices(field.options ?? [], definitionOf(field)?.choices).length > 0);
    case "PROFILE_FORM_FIELD_IMMUTABLE":
      return find(field => !!saved[field.id] && (saved[field.id].type !== field.type || saved[field.id].target !== targetValue(field.target)));
    default:
      return null;
  }
};

// Every text of the form in the default language, as the translations API names it
export const profileFormSourceTexts = (sections: ProfileFormSectionDto[]): ProfileFormSourceText[] =>
  sections.flatMap(section => [
    { entityType: "profile_form_section" as const, entityId: section.id, field: "title", value: section.title },
    { entityType: "profile_form_section" as const, entityId: section.id, field: "description", value: section.description ?? "" },
    ...section.fields.flatMap(field => [
      { entityType: "profile_form_field" as const, entityId: field.id, field: "label", value: field.label },
      { entityType: "profile_form_field" as const, entityId: field.id, field: "help", value: field.help ?? "" },
      ...(field.options ?? []).map(option => ({ entityType: "profile_form_option" as const, entityId: option.id, field: "label", value: option.label })),
    ]),
  ]).filter(({ value }) => !!value.trim());

// How much of the form each other locale translates: only texts filled in the default language count
export const profileFormTranslationStatus = (
  sources: ProfileFormSourceText[],
  textsByLocale: Record<string, ProfileFormLocaleTexts>,
): Record<string, TranslationStatus> =>
  Object.fromEntries(
    Object.entries(textsByLocale).map(([locale, texts]) => {
      if (!sources.length) return [locale, "complete"];
      const filled = sources.filter(({ entityId, field }) => !!texts[entityId]?.[field]?.trim()).length;
      return [locale, filled === 0 ? "empty" : filled >= sources.length ? "complete" : "partial"];
    }),
  );

// The form as a customer of that locale reads it: each text translated, or the default one
export const localizeProfileForm = (sections: ProfileFormSectionDto[], texts: ProfileFormLocaleTexts | null): ProfileFormSectionDto[] => {
  const text = (entityId: string, field: string, fallback: string | undefined): string => texts?.[entityId]?.[field]?.trim() || (fallback ?? "");
  return sections.map(section => ({
    ...section,
    title: text(section.id, "title", section.title),
    description: text(section.id, "description", section.description),
    fields: section.fields.map(field => ({
      ...field,
      label: text(field.id, "label", field.label),
      help: text(field.id, "help", field.help),
      options: field.options?.map(option => ({ ...option, label: text(option.id, "label", option.label) })),
    })),
  }));
};

// Worst of two statuses per locale (a toolbar shows one per language)
export const mergeTranslationStatus = (
  a: Record<string, TranslationStatus>,
  b: Record<string, TranslationStatus>,
): Record<string, TranslationStatus> =>
  Object.fromEntries(
    [...new Set([...Object.keys(a), ...Object.keys(b)])].map(locale => {
      const statuses = [a[locale], b[locale]].filter((status): status is TranslationStatus => !!status);
      if (statuses.every(status => status === "complete")) return [locale, "complete"];
      if (statuses.every(status => status === "empty")) return [locale, "empty"];
      return [locale, "partial"];
    }),
  );
